"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthServiceLive = void 0;
/* eslint-disable no-undef */
const mystake_auth_frontend_sdk_1 = require("@redbelly-tokeniser/mystake-auth-frontend-sdk");
const avererBffSessionStorage_1 = require("../utils/avererBffSessionStorage");
const HttpProvider_1 = require("../utils/HttpProvider");
const constants_1 = require("../utils/constants");
const errors_1 = require("../errors");
const IAuthService_1 = require("../contracts/IAuthService");
const SessionService_1 = require("./SessionService");
const toAvererSessionUser = (payload) => ({
    uid: payload.accountId,
    email: payload.email ?? null,
    avererAccountId: payload.accountId,
});
class AuthServiceLive {
    constructor(firebaseAuthConfig) {
        this.sessionService = SessionService_1.SessionService.create();
        this.authService = mystake_auth_frontend_sdk_1.FirebaseAuthService.getInstance(firebaseAuthConfig);
        this.isNodeEnv = typeof process !== "undefined" && process.versions != null && process.versions.node != null;
        this.authService.onAuthStateChanged((user) => {
            // Averer BFF sessions live in an HttpOnly cookie (not Firebase), so only the
            // Firebase-backed tokeniser session-id needs clearing when Firebase signs out.
            if (!user && !this.isNodeEnv) {
                this.sessionService.removeCurrentSessionId();
            }
        });
    }
    async clearAvererBffSessionClientAndServer() {
        (0, avererBffSessionStorage_1.clearAvererBffSession)();
        try {
            await HttpProvider_1.httpProvider.post("/auth/logout", {});
        }
        catch {
            // ignore when no Averer cookie is present or the logout call fails
        }
    }
    async loginWithUsernameAndPassword(username, password, rememberMe, usePassword) {
        const res = await HttpProvider_1.httpProvider.post("/user/account-status", {
            username,
            password,
            ...(usePassword && { usePassword: true }),
        });
        // An account that has moved to Averer is answered here and goes no further: signing in with
        // Firebase afterwards would hand back the session the service has just refused. The payload
        // field is what decides, so the wording of the message is free to change.
        if ((0, IAuthService_1.isAvererSignInRequired)(res.data)) {
            return {
                status: false,
                message: res.data.message,
                payload: { signInMethod: "averer" },
            };
        }
        if (res.data.status === false) {
            return {
                status: res.data.status,
                message: res.data.message,
                payload: res.data.payload,
            };
        }
        const accountStatus = res.data.payload;
        if (accountStatus.isInactive) {
            throw new errors_1.BadRequest("Password is incorrect.");
        }
        await this.clearAvererBffSessionClientAndServer();
        await this.authService.setAuthPersistence(Boolean(rememberMe));
        const user = await this.authService.loginWithEmail(accountStatus.email, password);
        if (user) {
            await this.sessionService.createSession({ userId: user.user.uid, rememberMe: Boolean(rememberMe) });
        }
        return {
            status: true,
            message: "Login successful",
            payload: null,
        };
    }
    async isPasswordSignInAvailable(identifier) {
        // The body carries the identifier only. A password, or a remember-me choice, has no part in
        // deciding which second step to show, and this call happens before either is collected.
        let response;
        try {
            response = await HttpProvider_1.httpProvider.post("/user/password-sign-in/check", { identifier });
        }
        catch {
            // A dropped connection or a timeout tells the caller nothing about the account, so it is
            // treated exactly like a refusal: no password step.
            return false;
        }
        // httpProvider resolves for every status, so a 404 (option switched off) and a 429 (refused
        // burst) arrive here as ordinary responses. Only an explicit affirmative answer opens the
        // password step; anything else, including a malformed body, keeps it shut.
        const payload = response?.status === 200 && response?.data?.status === true ? response?.data?.payload : undefined;
        return payload?.passwordSignInAvailable === true;
    }
    async getAvererInvitation(type, invitationToken) {
        const params = new URLSearchParams({ type, invitation_token: invitationToken });
        const response = await HttpProvider_1.httpProvider.get(`/verify/invite/details?${params.toString()}`);
        if (response?.status !== 200 || !response?.data) {
            throw new Error(response?.data?.message || "Could not load the invitation");
        }
        return response.data;
    }
    async startAvererSignIn(returnTo, options) {
        // BFF: full-page navigation to the API, which runs discovery + PKCE server-side
        // and 302s to the OP. The browser never fetches the OP directly (no CORS needed).
        if (typeof window === "undefined") {
            throw new Error("startAvererSignIn requires a browser environment");
        }
        const apiBaseUrl = (0, HttpProvider_1.getHttpProviderConfig)().baseUrl.replace(/\/$/, "");
        const target = returnTo && returnTo.startsWith("/") ? returnTo : "/";
        const params = new URLSearchParams({ return_to: target });
        // Forces the OP to re-show its login screen instead of silently reusing an
        // existing OP session cookie (silent SSO), so a different account can be entered.
        if (options?.forceAccountSelection) {
            params.set("prompt", "login");
        }
        if (options?.invitationToken) {
            params.set("invitation_token", options.invitationToken);
        }
        window.location.href = `${apiBaseUrl}/auth/oidc/start?${params.toString()}`;
    }
    async completeAvererSignIn() {
        // Web-relay BFF: the OP redirected the browser to the web /oidc/callback with the
        // authorization code. Relay code + state to the API, which does the confidential
        // token exchange + id_token verification server-side and sets the session cookie.
        // The browser never sees a token.
        if (typeof window === "undefined") {
            return { status: false, message: "browser required", payload: null };
        }
        const params = new URLSearchParams(window.location.search);
        const providerError = params.get("error");
        if (providerError) {
            return { status: false, message: providerError, payload: null };
        }
        const code = params.get("code");
        const state = params.get("state");
        if (!code || !state) {
            return { status: false, message: "invalid_request", payload: null };
        }
        const response = await HttpProvider_1.httpProvider.post("/auth/oidc/complete", { code, state });
        const succeeded = response?.status === 200 && Boolean(response?.data?.status);
        if (!succeeded) {
            return { status: false, message: response?.data?.message || "callback_failed", payload: null };
        }
        // A lingering Firebase session would shadow the new Averer cookie (bearer wins server-side).
        await this.authService.signOut().catch(() => undefined);
        this.sessionService.removeCurrentSessionId();
        (0, avererBffSessionStorage_1.markAvererBffSessionActive)();
        const returnTo = typeof response?.data?.payload?.returnTo === "string" ? response.data.payload.returnTo : "/";
        return { status: true, message: "Login successful", payload: { returnTo } };
    }
    async signOut() {
        (0, avererBffSessionStorage_1.clearAvererBffSession)();
        await this.sessionService.revokeSession().catch(() => undefined);
        this.sessionService.removeCurrentSessionId();
        // Averer BFF logout: clears the server-side session cookie and returns the OP
        // RP-initiated logout URL when the caller had an Averer session.
        try {
            const response = await HttpProvider_1.httpProvider.post("/auth/logout", {});
            const endSessionUrl = response?.data?.payload?.endSessionUrl;
            if (endSessionUrl && typeof window !== "undefined") {
                await this.authService.signOut().catch(() => undefined);
                window.location.href = endSessionUrl;
                return;
            }
        }
        catch {
            // fall through to Firebase sign-out
        }
        await this.authService.signOut();
    }
    async signUp(user, selectedSubscriptionPlan, selectedTier) {
        const result = await HttpProvider_1.httpProvider.post(constants_1.CREATE_ACCOUNT, {
            user,
            selectedSubscriptionPlan,
            selectedTier,
        });
        await this.authService.setAuthPersistence(true);
        await this.clearAvererBffSessionClientAndServer();
        const newUser = await this.authService.signInWithCustomToken(result.data.payload.customToken);
        if (newUser) {
            await this.sessionService.createSession({ userId: newUser.user.uid, rememberMe: true });
        }
        const authToken = await newUser.user.getIdToken();
        return authToken;
    }
    async currentUser() {
        if ((0, avererBffSessionStorage_1.isAvererBffSessionActive)()) {
            try {
                const response = await HttpProvider_1.httpProvider.get("/auth/me");
                const payload = response?.status === 200 ? response?.data?.payload : undefined;
                if (payload?.accountId) {
                    return toAvererSessionUser(payload);
                }
            }
            catch {
                // ignore when no Averer session cookie is present
            }
            (0, avererBffSessionStorage_1.clearAvererBffSession)();
            return null;
        }
        const firebaseUser = await this.authService.currentUser();
        if (firebaseUser) {
            return firebaseUser;
        }
        try {
            const response = await HttpProvider_1.httpProvider.get("/auth/me");
            const payload = response?.status === 200 ? response?.data?.payload : undefined;
            if (payload?.accountId) {
                (0, avererBffSessionStorage_1.markAvererBffSessionActive)();
                return toAvererSessionUser(payload);
            }
        }
        catch {
            // ignore when no Averer session cookie is present
        }
        return null;
    }
    async getIdToken() {
        // Averer BFF users authenticate via the session cookie, not a bearer token.
        const authToken = await this.authService.getIdToken();
        return authToken;
    }
    /**
     * Reads the custom-token acceptance payload, or throws the API's own failure envelope.
     *
     * `httpProvider` resolves for every HTTP status (see HttpService's `validateStatus`), so a
     * refused acceptance (wrong password, enforced-invitation refusal, validation error) comes
     * back as a normal resolved response with `status: false` and `payload: null` — the same
     * `payload: null` shape a *successful* enforced profile-step reply uses. Only `status` tells
     * the two apart. Every caller in this file (`Register.tsx` and friends) wraps the SDK call in
     * try/catch and reads `error.response.data`, so a refusal must throw in that shape rather
     * than resolve, or the caller runs the success path anyway.
     */
    static requireAcceptedCustomToken(response) {
        if (response.data.status === false) {
            throw new errors_1.AcceptanceRefusedError(response);
        }
        return response.data.payload;
    }
    /**
     * Signs in with the Firebase custom token an accepted (password) acceptance returns, mirroring
     * `signUp`'s post-registration flow. Shared by all three create-account methods below: the only
     * difference between them is the endpoint called and the request body shape.
     */
    async signInWithAcceptedToken(customToken) {
        await this.authService.setAuthPersistence(true);
        await this.clearAvererBffSessionClientAndServer();
        const userCredentials = await this.authService.signInWithCustomToken(customToken);
        if (userCredentials) {
            await this.sessionService.createSession({ userId: userCredentials.user.uid, rememberMe: true });
        }
        const authToken = await userCredentials.user.getIdToken();
        return {
            status: true,
            message: "Account creation and login successful",
            payload: authToken,
        };
    }
    /**
     * Common tail of all three create-account methods: throw on a refused acceptance, answer with
     * an empty payload for a password-less enforced profile step (no Firebase credential to sign
     * into — the browser already holds the Averer session cookie), or sign into the token a
     * password-based acceptance returns.
     */
    async completeAcceptance(response) {
        const customToken = AuthServiceLive.requireAcceptedCustomToken(response);
        if (!customToken) {
            return {
                status: true,
                message: response.data.message,
                payload: "",
            };
        }
        return this.signInWithAcceptedToken(customToken);
    }
    async createNomineeAccount(userId, createNomineeAccountRequestBody) {
        const response = await HttpProvider_1.httpProvider.patch(`/nominee/user/${userId}`, createNomineeAccountRequestBody);
        return this.completeAcceptance(response);
    }
    async createInvestorAccount(userId, createInvestorRequestBody) {
        const response = await HttpProvider_1.httpProvider.patch(`/investor/user/${userId}`, createInvestorRequestBody);
        return this.completeAcceptance(response);
    }
    async createAdminAccount(adminUserId, companyId, createNewAdminAccountRequestBody) {
        const response = await HttpProvider_1.httpProvider.post(`/admin/${companyId}/user/${adminUserId}`, createNewAdminAccountRequestBody);
        return this.completeAcceptance(response);
    }
}
exports.AuthServiceLive = AuthServiceLive;
//# sourceMappingURL=AuthServiceLive.js.map